import React, { useContext, useState } from 'react';
import useForm from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { FedexCommodityInDTO, FedexCustomsInfoInDTO } from '@api/ringil-api.v2.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon.tsx';
import SvgRoute from '@icons/route.svg?react';
import SvgTruck from '@icons/truck.svg?react';
import SvgInfoCircle from '@icons/info.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import InputQuantityWithUnit from '@components/RingilComponents/form/InputQuantityWithUnit.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import FedexDisclaimer from '@components/RingilComponents/FedexDisclaimer.tsx';
import FedexLogo from '@components/RingilComponents/FedexLogo.tsx';

interface ComponentProps {
}

interface SidebarData {
  quoteDemandId: string;
  expeditionApplicationId: string;
  carrierName: string;
  data?: FedexCustomsInfoInDTO;
  onContinue: () => void;
  isEditable: boolean;
  isDeletable: boolean;
  reloadData?: () => void;
}

export const useFedexQuoteAdditionalSidebar = () => {
  const [sidebarData, setSidebarData] = useState<SidebarData>(undefined);

  const FedexQuoteAdditionalSidebar: React.FC<ComponentProps> = () => {
    const { saveFedexAdditionalData, deleteFedexAdditionalData } = useContext(ApiContext);
    const { tr } = useTranslationLgs();
    const { i18n } = useTranslation();

    const formSchema = yup.object().shape({
      totalCustomsValue: yup
        .number()
        .typeError(tr('Form.priceMustBeNumber', 'Price must be a number'))
        .required(tr('Form.totalCustomsValueRequired', 'Total customs value is required')),
      currency: yup.string().required(tr('Form.currencyRequired', 'Currency is required')),
      shipmentPurpose: yup.string().required(tr('Form.shipmentPurposeRequired', 'Shipment purpose is required')),
      consumerRecipient: yup.boolean(),
      commodities: yup.array().of(
        yup.object().shape({
          description: yup.string().required(tr('Form.descriptionRequired', 'Description is required')),
          // EU product identifiers: M-PID + NS-PID are mandatory for B2C imports into the EU, S-PID only if it exists
          merchantProductId: yup.string().when('$formData.consumerRecipient', {
            is: true,
            then: schema => schema.required(tr('Form.merchantProductIdRequired', 'Merchant product ID (SKU) is required')),
            otherwise: schema => schema.nullable(),
          }),
          manufacturerProductId: yup.string().when('$formData.consumerRecipient', {
            is: true,
            then: schema => schema.required(tr('Form.manufacturerProductIdRequired', 'Manufacturer product ID is required')),
            otherwise: schema => schema.nullable(),
          }),
          standardProductId: yup.string().nullable(),
          harmonizedCode: yup.string().required(tr('Form.harmonizedCodeRequired', 'Harmonized (HS) code is required')),
          countryOfManufacture: yup.string().required(tr('Form.manufacturerCountryRequired', 'Country code is required')),
          quantity: yup
            .number()
            .typeError(tr('Form.quantityMustBeNumber', 'Quantity must be a number'))
            .required(tr('Form.quantityRequired', 'Quantity is required')),
          quantityUnits: yup.string().required(tr('Form.unitRequired', 'Unit is required')),
          unitPrice: yup
            .number()
            .typeError(tr('Form.priceMustBeNumber', 'Price must be a number'))
            .required(tr('Form.priceRequired', 'Price per piece is required')),
          weightKg: yup
            .number()
            .typeError(tr('Form.weightMustBeNumber', 'Weight must be a number'))
            .required(tr('Form.weightRequired', 'Weight is required')),
        }),
      ),
    });

    const getInitialCommodity = (): FedexCommodityInDTO => ({
      description: '',
      harmonizedCode: '',
      countryOfManufacture: '',
      quantity: null,
      quantityUnits: 'PCS',
      unitPrice: null,
      weightKg: null,
      merchantProductId: '',
      manufacturerProductId: '',
      standardProductId: '',
    });

    const initialValues: FedexCustomsInfoInDTO = {
      totalCustomsValue: null,
      currency: 'CZK',
      shipmentPurpose: 'SOLD',
      consumerRecipient: false,
      commodities: [getInitialCommodity()],
      dutiesPaymentType: 'SENDER',
      isDocumentOnly: false,
      electronicTradeDocuments: true,
    };

    const shipmentPurposeOptions = [
      { label: tr('FedexQuoteAdditionalSidebar.purpose.SOLD', 'Sold'), value: 'SOLD' },
      { label: tr('FedexQuoteAdditionalSidebar.purpose.GIFT', 'Gift'), value: 'GIFT' },
      { label: tr('FedexQuoteAdditionalSidebar.purpose.NOT_SOLD', 'Not sold'), value: 'NOT_SOLD' },
      { label: tr('FedexQuoteAdditionalSidebar.purpose.PERSONAL_EFFECTS', 'Personal effects'), value: 'PERSONAL_EFFECTS' },
      { label: tr('FedexQuoteAdditionalSidebar.purpose.REPAIR_AND_RETURN', 'Repair and return'), value: 'REPAIR_AND_RETURN' },
      { label: tr('FedexQuoteAdditionalSidebar.purpose.SAMPLE', 'Sample'), value: 'SAMPLE' },
    ];

    const form = useForm<FedexCustomsInfoInDTO>(
      formSchema,
      sidebarData?.data || initialValues,
      formData => {
        saveFedexAdditionalData(sidebarData?.quoteDemandId, formData, () => {
          sidebarData?.reloadData();
          setSidebarData(null);
          sidebarData.onContinue();
        });
      },
      false,
      false,
    );

    if (isNullOrUndefined(sidebarData)) return null;

    const totalQuantity = form.values.commodities.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalWeight = form.values.commodities.reduce((sum, item) => sum + (item.weightKg || 0), 0);
    const totalValue = form.values.commodities.reduce((sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0), 0);
    const customsValueMismatch =
      isNotNullOrUndefined(form.values.totalCustomsValue) && Math.abs(totalValue - form.values.totalCustomsValue) > 0.01;
    const formatNumber = (value: number, maxFractionDigits: number) =>
      value.toLocaleString(i18n.language, { maximumFractionDigits: maxFractionDigits });

    return (
      <RingilSidebar
        visible={isNotNullOrUndefined(sidebarData)}
        heading={tr(`FedexQuoteAdditionalSidebar.title`, `Add FedEx customs information`)}
        onBack={() => setSidebarData(null)}
        continueLabel={tr(`Form.continueLabel`, `Continue`)}
        onContinue={() => form.validateAndSend()}
        onDeleteForm={sidebarData.isDeletable ? () => {
          form.setValues?.(initialValues);
          form.clear();
          if (!sidebarData?.isEditable) {
            deleteFedexAdditionalData(sidebarData?.quoteDemandId, () => {
              setSidebarData(null);
              sidebarData?.reloadData();
            });
          }
          }
          : null
        }
        showSaveButton={sidebarData?.isEditable}
        maxWidth="1100px"
      >
        <div className="flex flex-col gap-4">
          <FedexLogo height={40} className="self-start" />
          <VerticalFieldAndValueWithIcon
            label={tr('Form.forExpedition', 'For expedition')}
            valueText={sidebarData.expeditionApplicationId}
            icon={<SvgRoute />}
          />
          <VerticalFieldAndValueWithIcon
            label={tr('Form.selectedCarrier', 'Selected carrier')}
            valueText={sidebarData.carrierName}
            icon={<SvgTruck />}
          />
          <COInlineMessage
            variant={'info'}
            icon={<SvgInfoCircle className="text-content-tertiary" />}
          >
            {tr('QuoteAdditionalInfo.additionalInfoRequiredMessage','The carrier requires this information for the transport of the cargo')}
          </COInlineMessage>

          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-4">
              <div className="w-[500px]">
                <Field
                  label={tr('Form.totalCustomsValue', 'Total customs value')}
                  required={!form.values.totalCustomsValue}
                  errorMessage={findErrorMessage(form.validationErrors, form.names.totalCustomsValue, tr)}
                  disabled={!sidebarData?.isEditable}
                  skipCurrencyCssMode
                >
                  <InputPriceWithCurrency
                    value={form.values.totalCustomsValue}
                    currency={form.values.currency}
                    changePrice={v => form.setFieldValue(form.names.totalCustomsValue, v)}
                    changeCurrency={v => form.setFieldValue(form.names.currency, v)}
                  />
                </Field>
              </div>
              <Field
                label={tr('Form.shipmentPurpose', 'Shipment purpose')}
                required={!form.values.shipmentPurpose}
                errorMessage={findErrorMessage(form.validationErrors, form.names.shipmentPurpose, tr)}
                disabled={!sidebarData?.isEditable}
              >
                <Dropdown
                  options={shipmentPurposeOptions}
                  optionLabel="label"
                  optionValue="value"
                  value={form.values.shipmentPurpose}
                  onChange={e => form.setFieldValue(form.names.shipmentPurpose, e.value)}
                  style={{ width: '250px' }}
                />
              </Field>
            </div>
            {/* B2C import into the EU: EU customs require product identifiers per item from 2026-11-01 */}
            <InputCheckbox
              label={tr('FedexQuoteAdditionalSidebar.customs.consumerRecipient', 'Recipient is a private person (not VAT-registered, B2C)')}
              value={!!form.values.consumerRecipient}
              onChange={(_, value) => form.setFieldValue('consumerRecipient', !!value)}
              disabled={!sidebarData?.isEditable}
            />
            {form.values.consumerRecipient && (
              <COInlineMessage variant={'info'} icon={<SvgInfoCircle className="text-content-tertiary" />}>
                {tr(
                  'FedexQuoteAdditionalSidebar.customs.consumerRecipientHint',
                  'From 1 Nov 2026 EU customs require product identifiers for every item sold to EU consumers: merchant product ID (SKU) and manufacturer product ID are mandatory, EAN/GTIN only when the product has one.',
                )}
              </COInlineMessage>
            )}
            <div>
              {form.values.commodities.map((commodity, index) => (
                <div className={`flex flex-col p-4 pb-6 ${index % 2 === 0 ? 'bg-white' : 'bg-base-quaternary'}`}
                     key={index}>
                  <div className="flex flex-row justify-between items-center w-full">
                    <COHeadline
                      variant={'h4'}
                      title={tr('Form.itemNumber', 'Item nr. {{index}}', { index: index + 1 })}
                    />
                    <RingilButton
                      mode={'outlined-rectangle'}
                      icon={<SvgDelete />}
                      onClick={() => {
                        const newItems = form.values.commodities.filter((_, i) => i !== index);
                        form.setFieldValue(form.names.commodities, newItems);
                      }}
                    />
                  </div>
                  <div className="flex flex-row items-center w-full gap-4">
                    <Field
                      label={tr('Form.description', 'Description')}
                      required={!form.values.commodities[index].description}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].description`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'500px'}
                    >
                      <InputText
                        value={form.values.commodities[index].description}
                        onChange={e => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], description: e.target.value };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                      />
                    </Field>
                    <Field
                      label={tr('Form.quantity', 'Quantity')}
                      required={!form.values.commodities[index].quantity}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].quantity`, tr)}
                      disabled={!sidebarData?.isEditable}
                      skipCurrencyCssMode
                    >
                      <InputQuantityWithUnit
                        value={form.values.commodities[index].quantity}
                        unit={form.values.commodities[index].quantityUnits}
                        changeValue={quantity => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], quantity };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                        changeUnit={unit => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], quantityUnits: unit };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                        unitOptions={[
                          { label: tr('Form.unit.pcs', 'PCS'), value: 'PCS' },
                          { label: tr('Form.unit.ea', 'EA'), value: 'EA' },
                        ]}
                      />
                    </Field>
                    <Field
                      label={tr('Form.pricePerPiece', 'Price per piece')}
                      required={!form.values.commodities[index].unitPrice}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].unitPrice`, tr)}
                      disabled={!sidebarData?.isEditable}
                    >
                      <InputNumber
                        value={form.values.commodities[index].unitPrice}
                        onChange={e => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], unitPrice: e.value };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        locale={i18n.language}
                      />
                    </Field>
                    <Field
                      label={tr('Form.countryCode', 'Country code')}
                      required={!form.values.commodities[index].countryOfManufacture}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].countryOfManufacture`, tr)}
                      disabled={!sidebarData?.isEditable}
                    >
                      <InputCountry
                        value={form.values.commodities[index].countryOfManufacture}
                        onChange={v => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], countryOfManufacture: v };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                      />
                    </Field>
                  </div>

                  <div className="flex flex-row items-center w-full gap-4">
                    <Field
                      label={tr('Form.harmonizedCode', 'Harmonized (HS) code')}
                      required={!form.values.commodities[index].harmonizedCode}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].harmonizedCode`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'500px'}
                    >
                      <InputText
                        value={form.values.commodities[index].harmonizedCode}
                        onChange={e => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], harmonizedCode: e.target.value };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                      />
                    </Field>
                    <Field
                      label={tr('Form.weightKg', 'Weight (kg)')}
                      required={!form.values.commodities[index].weightKg}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].weightKg`, tr)}
                      disabled={!sidebarData?.isEditable}
                    >
                      <InputNumber
                        value={form.values.commodities[index].weightKg}
                        onChange={e => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], weightKg: e.value };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                        minFractionDigits={0}
                        maxFractionDigits={3}
                        locale={i18n.language}
                      />
                    </Field>
                  </div>

                  {/* EU product identifiers (M-PID / NS-PID / S-PID) */}
                  <div className="flex flex-row items-center w-full gap-4">
                    <Field
                      label={tr('Form.merchantProductId', 'Merchant product ID (SKU)')}
                      required={!!form.values.consumerRecipient && !form.values.commodities[index].merchantProductId}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].merchantProductId`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'320px'}
                    >
                      <InputText
                        value={form.values.commodities[index].merchantProductId ?? ''}
                        onChange={e => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], merchantProductId: e.target.value };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                      />
                    </Field>
                    <Field
                      label={tr('Form.manufacturerProductId', 'Manufacturer product ID (part number)')}
                      required={!!form.values.consumerRecipient && !form.values.commodities[index].manufacturerProductId}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].manufacturerProductId`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'320px'}
                    >
                      <InputText
                        value={form.values.commodities[index].manufacturerProductId ?? ''}
                        onChange={e => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], manufacturerProductId: e.target.value };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                      />
                    </Field>
                    <Field
                      label={tr('Form.standardProductId', 'EAN / GTIN / UPC / ISBN (if exists)')}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.commodities}[${index}].standardProductId`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'320px'}
                    >
                      <InputText
                        value={form.values.commodities[index].standardProductId ?? ''}
                        onChange={e => {
                          const newItems = [...form.values.commodities];
                          newItems[index] = { ...newItems[index], standardProductId: e.target.value };
                          form.setFieldValue(form.names.commodities, newItems);
                        }}
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <RingilButton
                mode={'link'}
                label={tr('Form.addItem', 'Add item')}
                icon={<SvgPlus />}
                onClick={() => {
                  form.setFieldValue(form.names.commodities, [...form.values.commodities, getInitialCommodity()]);
                }}
                disabled={!sidebarData?.isEditable}
              />
            </div>
            <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-content-quaternary">
              <COHeadline variant={'h4'} title={tr('FedexQuoteAdditionalSidebar.customs.title', 'Customs details')} />

              <div className="flex flex-row items-end gap-4 flex-wrap">
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.dutiesPayer', 'Duties and taxes paid by')} disabled={!sidebarData?.isEditable}>
                  <Dropdown
                    options={[
                      { label: tr('FedexQuoteAdditionalSidebar.customs.dutiesPayer.SENDER', 'Sender'), value: 'SENDER' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.dutiesPayer.RECIPIENT', 'Recipient'), value: 'RECIPIENT' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.dutiesPayer.THIRD_PARTY', 'Third party'), value: 'THIRD_PARTY' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    value={form.values.dutiesPaymentType ?? 'SENDER'}
                    onChange={e => form.setFieldValue('dutiesPaymentType', e.value)}
                    style={{ width: '200px' }}
                  />
                </Field>
                {form.values.dutiesPaymentType && form.values.dutiesPaymentType !== 'SENDER' && (
                  <>
                    <Field label={tr('FedexQuoteAdditionalSidebar.customs.dutiesPayerAccount', 'Payer FedEx account number')} disabled={!sidebarData?.isEditable}>
                      <InputText
                        value={form.values.dutiesPayerAccountNumber ?? ''}
                        onChange={e => form.setFieldValue('dutiesPayerAccountNumber', e.target.value)}
                        maxLength={9}
                      />
                    </Field>
                    <Field label={tr('FedexQuoteAdditionalSidebar.customs.dutiesPayerCountry', 'Payer country')} disabled={!sidebarData?.isEditable}>
                      <InputCountry
                        value={form.values.dutiesPayerCountry ?? ''}
                        onChange={v => form.setFieldValue('dutiesPayerCountry', v)}
                      />
                    </Field>
                  </>
                )}
              </div>

              <div className="flex flex-row items-end gap-4 flex-wrap">
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.comments', 'Commercial invoice comments')} disabled={!sidebarData?.isEditable} width={'500px'}>
                  <InputText
                    value={form.values.comments ?? ''}
                    onChange={e => form.setFieldValue('comments', e.target.value)}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.originatorName', 'Originator name')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.originatorName ?? ''}
                    onChange={e => form.setFieldValue('originatorName', e.target.value)}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.invoiceReference', 'Invoice customer reference')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.invoiceCustomerReference ?? ''}
                    onChange={e => form.setFieldValue('invoiceCustomerReference', e.target.value)}
                  />
                </Field>
              </div>

              <div className="flex flex-row items-end gap-4 flex-wrap">
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.taxesCharge', 'Taxes / miscellaneous charge')} disabled={!sidebarData?.isEditable} skipCurrencyCssMode>
                  <InputPriceWithCurrency
                    value={form.values.taxesOrMiscellaneousChargeAmount}
                    currency={form.values.taxesOrMiscellaneousChargeCurrency ?? form.values.currency}
                    changePrice={v => form.setFieldValue('taxesOrMiscellaneousChargeAmount', v)}
                    changeCurrency={v => form.setFieldValue('taxesOrMiscellaneousChargeCurrency', v)}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.insuranceCharge', 'Insurance costs on invoice (not provided by FedEx)')} disabled={!sidebarData?.isEditable} skipCurrencyCssMode>
                  <InputPriceWithCurrency
                    value={form.values.insuranceChargeAmount}
                    currency={form.values.insuranceChargeCurrency ?? form.values.currency}
                    changePrice={v => form.setFieldValue('insuranceChargeAmount', v)}
                    changeCurrency={v => form.setFieldValue('insuranceChargeCurrency', v)}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.freightCharge', 'Freight charge on invoice')} disabled={!sidebarData?.isEditable} skipCurrencyCssMode>
                  <InputPriceWithCurrency
                    value={form.values.freightChargeAmount}
                    currency={form.values.freightChargeCurrency ?? form.values.currency}
                    changePrice={v => form.setFieldValue('freightChargeAmount', v)}
                    changeCurrency={v => form.setFieldValue('freightChargeCurrency', v)}
                  />
                </Field>
              </div>

              <div className="flex flex-row items-end gap-4 flex-wrap">
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.recipientIdType', 'Recipient customs ID type')} disabled={!sidebarData?.isEditable}>
                  <Dropdown
                    options={[
                      { label: tr('FedexQuoteAdditionalSidebar.customs.recipientIdType.PASSPORT', 'Passport'), value: 'PASSPORT' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.recipientIdType.INDIVIDUAL', 'Individual'), value: 'INDIVIDUAL' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.recipientIdType.COMPANY', 'Company'), value: 'COMPANY' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    value={form.values.recipientCustomsIdType ?? null}
                    onChange={e => form.setFieldValue('recipientCustomsIdType', e.value ?? undefined)}
                    showClear
                    placeholder={tr('FedexQuoteAdditionalSidebar.customs.none', 'None')}
                    style={{ width: '200px' }}
                  />
                </Field>
                {form.values.recipientCustomsIdType && (
                  <Field label={tr('FedexQuoteAdditionalSidebar.customs.recipientIdValue', 'Recipient customs ID')} disabled={!sidebarData?.isEditable}>
                    <InputText
                      value={form.values.recipientCustomsIdValue ?? ''}
                      onChange={e => form.setFieldValue('recipientCustomsIdValue', e.target.value)}
                    />
                  </Field>
                )}
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.customsOption', 'Customs option')} disabled={!sidebarData?.isEditable}>
                  <Dropdown
                    options={[
                      { label: tr('FedexQuoteAdditionalSidebar.customs.customsOption.FOR_REPAIR', 'For repair'), value: 'FOR_REPAIR' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.customsOption.FOLLOWING_REPAIR', 'Following repair'), value: 'FOLLOWING_REPAIR' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.customsOption.REJECTED', 'Rejected'), value: 'REJECTED' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.customsOption.REPLACEMENT', 'Replacement'), value: 'REPLACEMENT' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.customsOption.OTHER', 'Other'), value: 'OTHER' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    value={form.values.customsOptionType ?? null}
                    onChange={e => form.setFieldValue('customsOptionType', e.value ?? undefined)}
                    showClear
                    placeholder={tr('FedexQuoteAdditionalSidebar.customs.none', 'None')}
                    style={{ width: '200px' }}
                  />
                </Field>
                {form.values.customsOptionType && (
                  <Field label={tr('FedexQuoteAdditionalSidebar.customs.customsOptionDescription', 'Customs option description')} disabled={!sidebarData?.isEditable}>
                    <InputText
                      value={form.values.customsOptionDescription ?? ''}
                      onChange={e => form.setFieldValue('customsOptionDescription', e.target.value)}
                    />
                  </Field>
                )}
              </div>

              <InputCheckbox
                label={tr('FedexQuoteAdditionalSidebar.customs.documentsOnly', 'Documents only shipment')}
                value={!!form.values.isDocumentOnly}
                onChange={(_, value) => form.setFieldValue('isDocumentOnly', !!value)}
                disabled={!sidebarData?.isEditable}
              />
              {/* ETD is the default; payloads saved before the flag existed have it undefined = true */}
              <InputCheckbox
                label={tr(
                  'FedexQuoteAdditionalSidebar.customs.etd',
                  'Electronic trade documents (ETD) - customs documents are sent to FedEx electronically',
                )}
                value={form.values.electronicTradeDocuments !== false}
                onChange={(_, value) => form.setFieldValue('electronicTradeDocuments', !!value)}
                disabled={!sidebarData?.isEditable}
              />

              <COHeadline variant={'h4'} title={tr('FedexQuoteAdditionalSidebar.customs.specialCargo', 'Special cargo')} />
              <div className="flex flex-row items-end gap-4 flex-wrap">
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.specialCargoType', 'Type')} disabled={!sidebarData?.isEditable}>
                  <Dropdown
                    options={[
                      { label: tr('FedexQuoteAdditionalSidebar.customs.specialCargo.BATTERY', 'Lithium batteries'), value: 'BATTERY' },
                      { label: tr('FedexQuoteAdditionalSidebar.customs.specialCargo.DANGEROUS_GOODS', 'Dangerous goods'), value: 'DANGEROUS_GOODS' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    value={form.values.specialCargo?.type ?? null}
                    onChange={e => form.setFieldValue('specialCargo', e.value ? { type: e.value } : undefined)}
                    showClear
                    placeholder={tr('FedexQuoteAdditionalSidebar.customs.none', 'None')}
                    style={{ width: '220px' }}
                  />
                </Field>
                {form.values.specialCargo?.type === 'BATTERY' && (
                  <>
                    <Field label={tr('FedexQuoteAdditionalSidebar.customs.batteryMaterial', 'Battery material')} disabled={!sidebarData?.isEditable}>
                      <Dropdown
                        options={[
                          { label: 'Lithium metal', value: 'LITHIUM_METAL' },
                          { label: 'Lithium ion', value: 'LITHIUM_ION' },
                        ]}
                        optionLabel="label"
                        optionValue="value"
                        value={form.values.specialCargo?.batteryMaterialType ?? 'LITHIUM_METAL'}
                        onChange={e => form.setFieldValue('specialCargo', { ...form.values.specialCargo, batteryMaterialType: e.value })}
                        style={{ width: '200px' }}
                      />
                    </Field>
                    <Field label={tr('FedexQuoteAdditionalSidebar.customs.batteryPacking', 'Battery packing')} disabled={!sidebarData?.isEditable}>
                      <Dropdown
                        options={[
                          { label: tr('FedexQuoteAdditionalSidebar.customs.batteryPacking.PACKED', 'Packed with equipment'), value: 'PACKED_WITH_EQUIPMENT' },
                          { label: tr('FedexQuoteAdditionalSidebar.customs.batteryPacking.CONTAINED', 'Contained in equipment'), value: 'CONTAINED_IN_EQUIPMENT' },
                        ]}
                        optionLabel="label"
                        optionValue="value"
                        value={form.values.specialCargo?.batteryPackingType ?? null}
                        onChange={e => form.setFieldValue('specialCargo', { ...form.values.specialCargo, batteryPackingType: e.value })}
                        style={{ width: '230px' }}
                      />
                    </Field>
                    <div className="text-xs text-content-tertiary pb-2">IATA Section II</div>
                  </>
                )}
                {form.values.specialCargo?.type === 'DANGEROUS_GOODS' && (
                  <>
                    <Field label={tr('FedexQuoteAdditionalSidebar.customs.dgAccessibility', 'Accessibility')} disabled={!sidebarData?.isEditable}>
                      <Dropdown
                        options={[
                          { label: tr('FedexQuoteAdditionalSidebar.customs.dgAccessibility.ACCESSIBLE', 'Accessible'), value: 'ACCESSIBLE' },
                          { label: tr('FedexQuoteAdditionalSidebar.customs.dgAccessibility.INACCESSIBLE', 'Inaccessible'), value: 'INACCESSIBLE' },
                        ]}
                        optionLabel="label"
                        optionValue="value"
                        value={form.values.specialCargo?.dgAccessibility ?? null}
                        onChange={e => form.setFieldValue('specialCargo', { ...form.values.specialCargo, dgAccessibility: e.value })}
                        style={{ width: '200px' }}
                      />
                    </Field>
                    <InputCheckbox
                      label={tr('FedexQuoteAdditionalSidebar.customs.dgCargoAircraftOnly', 'Cargo aircraft only')}
                      value={!!form.values.specialCargo?.dgCargoAircraftOnly}
                      onChange={(_, value) => form.setFieldValue('specialCargo', { ...form.values.specialCargo, dgCargoAircraftOnly: !!value })}
                      disabled={!sidebarData?.isEditable}
                    />
                  </>
                )}
              </div>

              <COHeadline variant={'h4'} title={tr('FedexQuoteAdditionalSidebar.customs.broker', 'Customs broker (Broker Select Option)')} />
              <div className="flex flex-row items-end gap-4 flex-wrap">
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.accountNumber', 'Broker FedEx account number')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.accountNumber ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, accountNumber: e.target.value })}
                    maxLength={9}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.companyName', 'Broker company')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.companyName ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, companyName: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.personName', 'Contact person')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.personName ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, personName: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.phone', 'Phone')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.phoneNumber ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, phoneNumber: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.email', 'Email')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.email ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, email: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.tin', 'Broker tax ID')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.tinNumber ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, tinNumber: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.street', 'Street')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.street ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, street: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.city', 'City')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.city ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, city: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.postalCode', 'Postal code')} disabled={!sidebarData?.isEditable}>
                  <InputText
                    value={form.values.broker?.postalCode ?? ''}
                    onChange={e => form.setFieldValue('broker', { ...form.values.broker, postalCode: e.target.value })}
                  />
                </Field>
                <Field label={tr('FedexQuoteAdditionalSidebar.customs.broker.country', 'Country')} disabled={!sidebarData?.isEditable}>
                  <InputCountry
                    value={form.values.broker?.country ?? ''}
                    onChange={v => form.setFieldValue('broker', { ...form.values.broker, country: v })}
                  />
                </Field>
              </div>
            </div>

            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-content-quaternary">
              <COHeadline variant={'h4'} title={tr('FedexQuoteAdditionalSidebar.summary', 'Summary')} />
              <div className="flex flex-row flex-wrap gap-x-8 gap-y-2">
                <div>
                  <div className="text-content-tertiary">{tr('FedexQuoteAdditionalSidebar.totalQuantity', 'Total quantity')}</div>
                  <div>{formatNumber(totalQuantity, 0)}</div>
                </div>
                <div>
                  <div className="text-content-tertiary">{tr('FedexQuoteAdditionalSidebar.totalWeight', 'Total weight (kg)')}</div>
                  <div>{formatNumber(totalWeight, 3)}</div>
                </div>
                <div>
                  <div className="text-content-tertiary">{tr('FedexQuoteAdditionalSidebar.totalValue', 'Total value')}</div>
                  <div>
                    {formatNumber(totalValue, 2)} {form.values.currency}
                  </div>
                </div>
              </div>
              {customsValueMismatch && (
                <COInlineMessage variant={'warning'} icon={<SvgInfoCircle className="text-content-tertiary" />}>
                  {tr(
                    'FedexQuoteAdditionalSidebar.customsValueMismatch',
                    'The total value of items ({{totalValue}} {{currency}}) does not match the total customs value ({{customsValue}} {{currency}}).',
                    {
                      totalValue: formatNumber(totalValue, 2),
                      customsValue: formatNumber(form.values.totalCustomsValue, 2),
                      currency: form.values.currency,
                    },
                  )}
                </COInlineMessage>
              )}
            </div>
          </div>
          <FedexDisclaimer />
        </div>
      </RingilSidebar>
    )
      ;
  };

  return { FedexQuoteAdditionalSidebar, setSidebarData };
};
